import { formatDistanceToNow } from "date-fns";
import { Calendar, FolderGit2, Star, User } from "lucide-react";
import { Badge } from "../../ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardMetadata,
  BaseEntityCardMetadataItem,
  BaseEntityCardProps,
  BaseEntityCardTags,
  BaseEntityCardTitle,
} from "../BaseCard";

export interface Repo {
  id: string;
  title: string;
  full_name: string;
  owner: string;
  description?: string;
  web_link?: string;
  url?: string;
  created_at?: string;
  updated_at?: string;
  stars_count?: number;
  forks_count?: number;
  language?: string;
  is_private?: boolean;
  is_fork?: boolean;
  is_archived?: boolean;
}

export type { Repo as Repository };

type RepositoryCardProps = Repo & BaseEntityCardProps;

// Avatar component for RepositoryCard
export function RepositoryCardAvatar({
  className,
  style,
}: Partial<RepositoryCardProps>) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      <FolderGit2 className="h-5 w-5 text-muted-foreground" />
    </BaseEntityCardIcon>
  );
}

export function RepositoryCard({
  platform,
  full_name,
  title,
  description = "",
  owner,
  web_link,
  updated_at,
  stars_count,
  language,
  is_private,
  is_fork,
  is_archived,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: RepositoryCardProps) {
  const formattedUpdatedDate = updated_at
    ? formatDistanceToNow(new Date(updated_at), { addSuffix: true })
    : "";

  const descriptionPreview =
    description && description.length > 100
      ? `${description.substring(0, 100)}...`
      : description;

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <RepositoryCardAvatar />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>
            {full_name || title}
          </BaseEntityCardTitle>
          <BaseEntityCardActions>
            {web_link && (
              <BaseEntityCardExternalLinkAction
                href={web_link}
                tooltip="Open repository"
              />
            )}
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          {descriptionPreview && (
            <BaseEntityCardDescription className="line-clamp-2 text-foreground">
              {descriptionPreview}
            </BaseEntityCardDescription>
          )}

          <BaseEntityCardTags>
            {owner && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <User className="h-3 w-3" />
                <span>{owner}</span>
              </div>
            )}

            {language && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className="text-xs bg-spectrum-blue-light/30 text-teal"
                    >
                      {language}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Primary Language</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {is_private && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className="text-xs bg-amber-100 text-amber-800"
                    >
                      Private
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Private Repository</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {is_fork && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className="text-xs bg-plum/10 text-purple-800"
                    >
                      Fork
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Forked Repository</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {is_archived && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className="text-xs bg-neutral-100 text-neutral-800"
                    >
                      Archived
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Archived Repository</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {typeof stars_count === "number" && stars_count > 0 && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Star className="h-3 w-3" />
                <span>{stars_count.toLocaleString()}</span>
              </div>
            )}
          </BaseEntityCardTags>

          {formattedUpdatedDate && (
            <BaseEntityCardMetadata>
              <BaseEntityCardMetadataItem
                icon={<Calendar className="h-3 w-3" />}
              >
                Updated {formattedUpdatedDate}
              </BaseEntityCardMetadataItem>
            </BaseEntityCardMetadata>
          )}
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
